import Plus from "assets/img/svg/plus";
import AutocompleteFormComponent, {
    defaultRenderOption,
} from "components/shared/FormComponent/AutocompleteFormComponent";
import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import InfoTooltip from "components/shared/InfoTooltip";
import PeopleAutocompleteField from "components/shared/PeopleAutocompleteField";
import TextButton from "components/shared/TextButton";
import TitleAutocompleteField from "components/shared/TitleAutocompleteField";
import type { ROLES } from "containers/ScopeContext/roles.enum";
import { formatRates } from "containers/ServicesPage/utils";
import TemplatesModal from "containers/TermFormContainer/ChannelsStep/TemplatesModal";
import { PortalType } from "contexts/portal.context";
import type { IsCompanyWithExceptions } from "hooks/useCompanySettings";
import { usePermissions } from "hooks/usePermissions";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArticleCompletionStatus } from "store/articleStore";
import type { IService } from "store/servicesStore";
import { useAppSelector } from "store/store";
import type { TermTemplate } from "store/termTemplatesStore";

import type { TArticleFormData } from "..";
import { completionStatusOptions } from "../utils";

interface INewArticleStep {
    stepTitle: string;
    stepSubtitle: string;
    indexStep: number;
    useFormData: TArticleFormData;
    usesIRCEC: boolean;
    services: IService[];
    mode: "new" | "edit";
    /**
     * Current user role
     */
    userRole: ROLES;
    /**
     * Check companies with exceptions
     */
    isCompany: IsCompanyWithExceptions;
    /**
     * Generic typologies (term_templates with beneficiaryId=null).
     */
    templates?: TermTemplate[];
    /**
     * Framework contracts (term_templates) of the current beneficiary.
     */
    frameworkContracts?: TermTemplate[];
    /**
     * Called when the user picks a typology in the modal. Parent applies the
     * relevant fields (channelId, folioPrice, workNature) to the form state.
     */
    onApplyTypology?: (template: TermTemplate) => void;
}

const NewArticleStep = ({
    stepTitle,
    stepSubtitle,
    indexStep,
    useFormData,
    usesIRCEC,
    services,
    mode,
    templates,
    frameworkContracts,
    onApplyTypology,
}: INewArticleStep) => {
    const [t] = useTranslation();
    const { control, errors, watch, setValue } = useFormData;
    const portalType = useContext(PortalType);
    const { beneficiarsAutocomplete } = useAppSelector((state) => state.beneficiary);

    const { hasPermissions } = usePermissions();

    const currentCompletionStatus = watch("completionStatus");
    const chosenPublication = watch("publication");
    const [templatesModalOpen, setTemplatesModalOpen] = useState(false);

    const hasAnyTypology =
        mode === "new" &&
        ((templates && templates.length > 0) || (frameworkContracts && frameworkContracts.length > 0));

    const handleApply = (template: TermTemplate) => {
        onApplyTypology?.(template);
        setTemplatesModalOpen(false);
    };

    const renderWorkNatureOption = (props, option: IService & { addElement?: string }) => {
        if (option.addElement) {
            return (
                <li {...props} key={option.id} className="autocomplete-new-element">
                    <div className="content">
                        <Plus />
                        {option.addElement}
                    </div>
                </li>
            );
        } else {
            return (
                <li {...props} key={option.id} className={`work-nature-option`}>
                    {option.name}
                    <div>
                        {option.hasIrcec ? (
                            <>
                                <span>{t("Services.Table.RACDValue", { value: formatRates(option.racd) })}</span>
                                <span>{t("Services.Table.RAAPValue", { value: formatRates(option.raap) })}</span>
                                <span>{t("Services.Table.SACDValue", { value: option.sacd })}</span>
                            </>
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                </li>
            );
        }
    };

    return (
        <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 className="form-section-title first">
                    {" "}
                    {indexStep + 1}. {stepTitle}
                    {mode === "edit" && (
                        <InfoTooltip
                            text={t("TransferTerms.Helpdesk.AccessDoc")}
                            link="https://presse-helpdesk.crealo.app/fr/article/modifier-un-article-pjy6tc/"
                            iconType="question"
                        />
                    )}
                </h3>
                {hasAnyTypology && (
                    <TextButton
                        text={t("TermTemplates.CompleteFromTemplate")}
                        handleClick={() => setTemplatesModalOpen(true)}
                        style={{ fontSize: "13px" }}
                    />
                )}
            </div>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                <TextFormComponent
                    label={t(`NewArticle.ArticleName_${portalType}`)}
                    placeholder={""}
                    control={control}
                    controlName="articleName"
                    error={errors.articleName}
                    required
                    disabled={!hasPermissions(["article:new"])}
                    customRequiredMessage={t("Articles.RequiredTitle")}
                />
                <TitleAutocompleteField
                    control={control}
                    controlName="publication"
                    required
                    type="publication"
                    errors={errors.publication}
                    chosenTitle={chosenPublication}
                    addButton={"Créer une parution"}
                    disabled={mode === "edit"}
                />
                <PeopleAutocompleteField
                    control={control}
                    errors={errors.beneficiar}
                    controlName="beneficiar"
                    label={t("NewArticle.Beneficiar")}
                    addButton={t("NewArticle.NewBeneficiary")}
                    required
                    customRequiredMessage={t("Articles.RequiredBeneficiary")}
                    type="beneficiaries"
                    watch={watch}
                    data={beneficiarsAutocomplete}
                    titleType="article"
                    disabled={mode === "edit"}
                />
                <TextFormComponent
                    label={t("NewArticle.WorkNature")}
                    placeholder={""}
                    control={control}
                    controlName="workNature"
                    error={errors.workNature}
                    disabled={!hasPermissions(["article:new"])}
                    showOptionalMark
                />
                {usesIRCEC && (
                    <AutocompleteFormComponent
                        control={control}
                        controlName="ircecProfile"
                        label={t("NewArticle.WorkNatureIRCEC")}
                        error={errors.ircecProfile}
                        required
                        options={services}
                        renderOptionText={(option) => option.name ?? ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        addElement={t("Services.Form.NewService")}
                        renderOption={renderWorkNatureOption}
                        disabled={!hasPermissions(["article:new"])}
                    />
                )}
                <AutocompleteFormComponent
                    control={control}
                    controlName="completionStatus"
                    label={t("NewArticle.CompletionStatus")}
                    required
                    disableClearable
                    options={completionStatusOptions()}
                    renderOption={defaultRenderOption}
                    renderOptionText={(option) => option.label ?? ""}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    disabled={!hasPermissions(["article:edit-completion-status"])}
                />
            </div>
            {currentCompletionStatus?.value === ArticleCompletionStatus.COMPLETED && (
                <div className="form-fields">
                    <DateFormComponent
                        control={control}
                        controlName="completionDate"
                        label={t("Articles.CompletionDate")}
                        error={errors.completionDate}
                        maxYearDistance={20}
                    />
                </div>
            )}
            <div className="form-fields" style={{ display: "none" }}></div>

            <TemplatesModal
                open={templatesModalOpen}
                onClose={() => setTemplatesModalOpen(false)}
                templates={templates}
                frameworkContracts={frameworkContracts}
                onSubmit={handleApply}
            />
        </>
    );
};

export default NewArticleStep;
